import { useEffect, useState } from "react";
import { AVATAR_COLORS } from "@kinop/shared";
import {
  getAvatar,
  setAvatar,
  getNickname,
  setNickname,
  getHistory,
  getRoomDefaults,
  saveRoomDefaults,
} from "../lib/storage.js";
import {
  getTelegramConfig,
  getTelegramMe,
  logoutTelegram,
  type TelegramConfig,
  type TelegramUser,
} from "../lib/api.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "../components/UserAvatar.js";
import { TelegramLogin } from "../components/TelegramLogin.js";
import { RoomRules } from "../components/RoomRules.js";
import { BadgePicker } from "../components/BadgePicker.js";
export function ProfilePage({
  onNavigate,
}: {
  onNavigate: (p: string) => void;
}): JSX.Element {
  const [name, setName] = useState(getNickname);
  const [avatar, setAv] = useState(getAvatar);
  const [rules, setRules] = useState(getRoomDefaults);
  const [account, setAccount] = useState<TelegramUser | null>(null);
  const [config, setConfig] = useState<TelegramConfig | null>(null);
  const [saved, setSaved] = useState("");
  const [devices, setDevices] = useState(false);
  const [history] = useState(getHistory);
  useEffect(() => {
    let done = false;
    void getTelegramConfig()
      .then((v) => {
        if (!done) setConfig(v);
      })
      .catch(() => {});
    void getTelegramMe()
      .then((v) => {
        if (!done) setAccount(v);
      })
      .catch(() => {});
    return () => {
      done = true;
    };
  }, []);
  function changeName(v: string) {
    setName(v);
    setNickname(v.trim());
    setSaved("Сохранено на этом устройстве");
  }
  return (
    <main className="shell">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h1>Профиль</h1>
        <span className="helper" role="status">
          {saved}
        </span>
      </div>
      <div className="profile-columns">
        <div>
          <section>
            <h2>Как вас видят</h2>
            <label htmlFor="profile-name" className="helper">
              Имя в комнате
            </label>
            <Input
              id="profile-name"
              value={name}
              maxLength={24}
              placeholder="Например, Саша"
              onChange={(e) => changeName(e.target.value)}
            />
            <p className="helper">
              {24 - name.length} символов осталось · можно прозвище
            </p>
            <div className="palette">
              {AVATAR_COLORS.map((color) => (
                <button
                  key={color}
                  aria-label={`Цвет ${color}`}
                  aria-pressed={avatar.color === color}
                  style={{
                    background: color,
                    outline:
                      avatar.color === color ? `2px solid ${color}` : "none",
                  }}
                  onClick={() => {
                    const v = { ...avatar, color, face: "?", photoUrl: null };
                    setAv(v);
                    setAvatar(v);
                    setSaved("Сохранено на этом устройстве");
                  }}
                />
              ))}
            </div>
          </section>
          <section className="card profile-preview">
            <h2>Telegram {account ? "привязан" : "не привязан"}</h2>
            {account ? (
              <>
                <p className="helper">
                  {account.username
                    ? `@${account.username}`
                    : account.displayName}
                </p>
                <Button
                  variant="secondary"
                  onClick={() =>
                    void logoutTelegram().then(() => {
                      setAccount(null);
                      setSaved("Telegram отключён");
                    })
                  }
                >
                  Отвязать
                </Button>
                {!account.botStarted && config?.botUsername && (
                  <p className="helper">
                    <a
                      href={`https://t.me/${config.botUsername}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Откройте бота
                    </a>{" "}
                    и нажмите «Старт», чтобы получать приглашения.
                  </p>
                )}
              </>
            ) : config?.enabled && config.botUsername ? (
              <TelegramLogin
                botUsername={config.botUsername}
                onLogin={(v) => {
                  setAccount(v);
                  changeName(v.displayName);
                  if (v.avatarUrl) {
                    const a = { ...avatar, photoUrl: v.avatarUrl };
                    setAv(a);
                    setAvatar(a);
                  }
                }}
              />
            ) : (
              <p className="helper">
                Вход через Telegram сейчас недоступен. Можно пользоваться своим
                именем.
              </p>
            )}
          </section>
          <section>
            <h2>Правила моих комнат</h2>
            <p className="helper">
              По умолчанию для новых сеансов. Внутри комнаты их можно изменить.
            </p>
            <RoomRules
              value={rules}
              onChange={(v) => {
                setRules(v);
                saveRoomDefaults(v);
                setSaved("Правила сохранены");
              }}
            />
          </section>
          <section>
            <h2>Аккаунт</h2>
            <Button variant="ghost" onClick={() => setDevices((v) => !v)}>
              Устройства и входы
            </Button>
            {devices && (
              <p className="helper">
                Текущее устройство ·{" "}
                {account ? "вход через Telegram" : "гостевой профиль"}. Имя,
                настройки и история хранятся в этом браузере.
              </p>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                void logoutTelegram();
                setNickname("");
                setAccount(null);
                onNavigate("/");
              }}
            >
              Выйти из аккаунта
            </Button>
          </section>
        </div>
        <div>
          <section className="card profile-preview">
            <p className="mono-label">Так вас видят в комнате</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <UserAvatar nickname={name || "Гость"} avatar={avatar} />
              <span>{name || "Гость"}</span>
              <span style={{ color: "var(--ok)", fontSize: 12 }}>
                в синхроне
              </span>
            </div>
            <p className="chat__text" style={{ marginTop: 24 }}>
              Ставьте, я готов{(name || "").endsWith("а") ? "а" : ""} 🍿
            </p>
          </section>
          <section>
            <h2>Куда вернуться</h2>
            {history.length ? (
              history.map((h) => (
                <article
                  key={h.code}
                  className="card"
                  style={{ padding: 16, marginBottom: 10 }}
                >
                  <h3>{h.name}</h3>
                  <p className="helper">
                    {h.title ?? "Видео не выбрано"} ·{" "}
                    {new Date(h.at).toLocaleDateString("ru-RU")}
                    <br />
                    {h.withWhom.join(", ")}
                  </p>
                  <div className="choice-row">
                    <button onClick={() => onNavigate(`/r/${h.code}`)}>
                      Вернуться
                    </button>
                    <button
                      onClick={() =>
                        onNavigate(`/new?name=${encodeURIComponent(h.name)}`)
                      }
                    >
                      Собрать снова
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <p className="helper">
                Здесь появятся комнаты, в которых вы смотрели кино.
              </p>
            )}
          </section>
          <section>
            <h2>Значок рядом с именем</h2>
            <BadgePicker
              badge={avatar.badge}
              onChange={(badge) => {
                const next = { ...avatar, badge };
                setAv(next);
                setAvatar(next);
                setSaved("Значок сохранён");
              }}
            />
          </section>
          <a
            className="download-link"
            href="/downloads/smotrim-vmeste.apk"
            download
          >
            ↓ Скачать приложение Android
          </a>
        </div>
      </div>
    </main>
  );
}
